import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CampaignCollorPallete} from '../../../../../components/CampaignCollorPallete';
import {BrandingService, OrchestratorColorsService} from '../../../../../services';
import {getBusinessByLogin} from '../../../../../services/business';
import {notifyErrorCustom} from '../../../../../utils/notify';
import sharedStyles from '../shared.module.css';

function hexToRgb(hexValue) {
  const cleanHex = hexValue.replace('#', '');
  if (cleanHex.length !== 6) {
    return null;
  }
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `(${r}, ${g}, ${b})`;
}

function rgbToHex(rgbValue) {
  const result = rgbValue?.match?.(/\d+/g);
  if (!result || result.length !== 3) {
    return '';
  }
  const toHex = (num) => parseInt(num, 10).toString(16).padStart(2, '0');
  return `#${toHex(result[0])}${toHex(result[1])}${toHex(result[2])}`;
}

function colorsFromBranding(row) {
  if (!row) {
    return [];
  }

  return [
    row.primaryColor,
    row.secondaryColor,
    row.tertiaryColor,
    row.complementaryColor1,
    row.complementaryColor2,
  ].filter(Boolean);
}

export default function PaletteSelector({
  isReadOnly = false,
  initialData = null,
  onComplete,
}) {
  const {t} = useTranslation();
  const [branding, setBranding] = useState(null);
  const [initialColors, setInitialColors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadBranding = useCallback(async () => {
    setLoading(true);
    try {
      const businessRes = await getBusinessByLogin();
      const businessId = businessRes?.data?.[0]?.uuid;

      if (!businessId) {
        if (initialData?.primaryColor) {
          setInitialColors(colorsFromBranding(initialData));
        }
        return;
      }

      const {data} = await BrandingService.getAllBrandingByBusinessId(businessId);
      if (data?.length > 0) {
        setBranding(data[0]);
        setInitialColors(colorsFromBranding(data[0]));
      } else if (initialData?.primaryColor) {
        setInitialColors(colorsFromBranding(initialData));
      }
    } finally {
      setLoading(false);
    }
  }, [initialData]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const paletteTitle = useMemo(
    () =>
      t(
        'campaignChat.parameterization.paletteTitle',
        'Escolha a paleta de cores da sua marca.',
      ),
    [t],
  );

  const handleConfirmPalette = async (colors) => {
    if (isReadOnly || saving) {
      return;
    }

    const filled = colors.filter(Boolean);
    if (filled.length < 3) {
      notifyErrorCustom(t('fast_publishing_choose_at_least_3_colors'));
      return;
    }

    setSaving(true);
    try {
      const [primaryColor, secondaryColor, tertiaryColor] = filled;
      let complementary1 = filled[3] || '';
      let complementary2 = filled[4] || '';

      if (!complementary1 || !complementary2) {
        const bodyGenerateArt = {
          color_palette_1: hexToRgb(primaryColor),
          color_palette_2: hexToRgb(secondaryColor),
          color_palette_3: hexToRgb(tertiaryColor),
          color_palette_4: complementary1 ? hexToRgb(complementary1) : '',
          color_palette_5: '',
        };
        const generateColorReturn =
          await OrchestratorColorsService.generateComplementarColors(
            bodyGenerateArt,
          );
        if (!complementary1) {
          complementary1 = rgbToHex(generateColorReturn?.data?.color_palette_4);
        }
        if (!complementary2) {
          complementary2 = rgbToHex(generateColorReturn?.data?.color_palette_5);
        }
      }

      const colorsArray = {
        primaryColor,
        secondaryColor,
        tertiaryColor,
        complementaryColor1: complementary1,
        complementaryColor2: complementary2,
        fontTitleId: branding?.fontTitleId || '',
        fontSubtitleId: branding?.fontSubtitleId || '',
        fontTextId: branding?.fontTextId || '',
        fontTitleByUploaded: branding?.fontTitleByUploaded ?? false,
        fontSubtitleByUploaded: branding?.fontSubtitleByUploaded ?? false,
        fontTextByUploaded: branding?.fontTextByUploaded ?? false,
      };

      if (branding?.uuid) {
        await BrandingService.putBranding(branding.uuid, colorsArray);
      } else {
        await BrandingService.postBranding(colorsArray);
      }

      onComplete?.({
        primaryColor,
        secondaryColor,
        tertiaryColor,
        complementaryColor1: complementary1,
        complementaryColor2: complementary2,
      });
    } catch {
      notifyErrorCustom(t('campaignChat.parameterization.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className={sharedStyles.subtitle}>
        {t('campaignChat.parameterization.loading')}
      </p>
    );
  }

  return (
    <div className={sharedStyles.container}>
      <CampaignCollorPallete
        embedded
        isReadOnly={isReadOnly}
        initialColors={initialColors}
        title={paletteTitle}
        confirmLabel={t('campaignChat.parameterization.confirm')}
        onConfirmPalette={handleConfirmPalette}
      />
      {isReadOnly && (
        <span className={sharedStyles.readOnlyBadge}>
          {t('campaignChat.parameterization.completed')}
        </span>
      )}
    </div>
  );
}
